import { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { StepPlan } from "../../../api/content/stepPlans";
import { listStartRouteOptions, type StartRouteQuestion } from "../../../api/threads/routeOptions";
import { readStartAutomaticHost } from "../../../api/threads/threadHostingApi";
import { useLoad } from "../../../shared/loading/useLoad";
import { picksLlm } from "./llmOptions";
import RunsWithRow from "./RunsWithRow";
import { draftWithDefault, draftWithStepRoute, startRoutesOf, type StartRouteDraft } from "./startRouteDraft";
import { StartRoutesContext, type StartRoutesValue } from "./startRoutesContext";
import { useLlmChoices } from "./useLlmChoices";


/** The start's question, the same object while its parts are; with the draft's routes when `draft` is given. */
function useQuestion(value: StartRoutesValue, draft: StartRouteDraft | null): StartRouteQuestion {
  const { projectId, workflow, contentVersion } = value;
  return useMemo(() => ({ projectId, workflow, contentVersion, routes: draft === null ? null : startRoutesOf(draft) }), [projectId, workflow, contentVersion, draft]);
}

/** The Thread's default for the start, on any of the person's online PCs; nothing outside a start. */
export function StartRoutes() {
  const value = useContext(StartRoutesContext);
  return value === null ? null : <StartDefault value={value} />;
}

function StartDefault({ value }: { value: StartRoutesValue }) {
  const { t } = useTranslation();
  const { draft, onDraftChange } = value;
  // The steps with their own route leave the default's fit, so the question carries them.
  const question = useQuestion(value, draft ?? null);
  const optionsOf = useCallback((id: string, signal: AbortSignal) => listStartRouteOptions(question, id, signal), [question]);
  const { state, refresh } = useLlmChoices("online", optionsOf);
  const choices = state.status === "loaded" ? state.value : [];
  // The first choice is automatic hosting's for this start, the same owner as for a Thread;
  // the preparation holds Start until it is read, so no start runs on an LLM nobody saw.
  const { projectId, workflow, contentVersion } = value;
  const readHost = useCallback((signal: AbortSignal) => readStartAutomaticHost({ projectId, workflow, contentVersion }, signal), [projectId, workflow, contentVersion]);
  const { state: host } = useLoad(readHost);
  useEffect(() => {
    if (draft !== undefined || host.status === "loading") return;
    const chosen = host.status === "loaded" ? host.value : null;
    // A start that asks has an agent step, so hosting's choice carries a route.
    onDraftChange(chosen === null || chosen.route === null ? null : { connectorId: chosen.connectorId, route: chosen.route, stepRoutes: {} });
  }, [draft, host, onDraftChange]);
  const differ = draft === null || draft === undefined ? 0 : Object.keys(draft.stepRoutes).length;

  return (
    <RunsWithRow
      route={draft?.route ?? null}
      connectorId={draft?.connectorId ?? null}
      note={differ === 0 ? null : t("threads.header.stepsDiffer", { count: differ })}
      choices={choices}
      read={state.status === "loaded"}
      onRefresh={refresh}
      mayPick={!value.disabled}
      own={false}
      onChoose={(choice) => { if (choice !== null) onDraftChange(draftWithDefault(draft ?? null, choice)); }}
      busy={false}
      error={state.status === "failed" ? state.error : null}
    />
  );
}

/** An agent step's own LLM for the start, on the start's PC; nothing for another step or outside a start. */
export function StartStepRoute({ plan }: { plan: StepPlan }) {
  const value = useContext(StartRoutesContext);
  if (value === null || value.draft === null || value.draft === undefined || plan.llmRequirements === null) return null;
  return <StartStep value={value} draft={value.draft} stepKey={plan.stepKey} picks={picksLlm(plan.llmRequirements)} />;
}

function StartStep({ value, draft, stepKey, picks }: { value: StartRoutesValue; draft: StartRouteDraft; stepKey: string; picks: boolean }) {
  const { t } = useTranslation();
  const own = draft.stepRoutes[stepKey];
  // With the draft's routes, so the server answers this step's own route too (CP-11), as the default's question does.
  const question = useQuestion(value, draft);
  const optionsOf = useCallback((id: string, signal: AbortSignal) => listStartRouteOptions(question, id, signal, stepKey), [question, stepKey]);
  const { state, refresh } = useLlmChoices([draft.connectorId], optionsOf);

  // The start's default is said once, above Start: a step says nothing more unless it differs (W-07).
  return (
    <RunsWithRow
      route={own ?? draft.route}
      connectorId={draft.connectorId}
      note={null}
      choices={state.status === "loaded" ? state.value : []}
      read={state.status === "loaded"}
      onRefresh={refresh}
      mayPick={picks && !value.disabled}
      own={own !== undefined}
      askLabel={t("threads.llm.ownForStep")}
      onChoose={(choice) => { value.onDraftChange(draftWithStepRoute(draft, stepKey, choice?.route ?? null)); }}
      busy={false}
      error={state.status === "failed" ? state.error : null}
    />
  );
}
